/**
 * Confirmations after a save. A short message appears at the bottom of the screen, is read
 * by screen readers (a polite live region that is always in the page, so the message is
 * announced when it arrives) and goes away by itself after a few seconds, or with its
 * close button. The countdown pauses while the pointer is on the message or focus is in it.
 * Errors are not toasts: they stay next to what caused them.
 */
import { CheckCircle2, X } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { IconButton } from "./index";
import s from "./ui.module.css";

type Toast = { id: number; message: string };

const ToastContext = createContext<((message: string) => void) | null>(null);

/** How long a confirmation stays, long enough to read a sentence (WCAG 2.2.1 allows closing it sooner). */
const SHOW_FOR_MS = 6000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null);
  const [paused, setPaused] = useState(false);
  const next = useRef(1);
  // Time left on the message shown, kept across pauses.
  const left = useRef({ id: 0, ms: SHOW_FOR_MS });

  const notify = useCallback((message: string) => {
    const id = next.current++;
    left.current = { id, ms: SHOW_FOR_MS };
    setPaused(false);
    setToast({ id, message });
  }, []);

  const close = () => {
    setPaused(false);
    setToast(null);
  };

  useEffect(() => {
    if (!toast || paused) return;
    const started = Date.now();
    const timer = setTimeout(() => setToast((now) => (now?.id === toast.id ? null : now)), left.current.id === toast.id ? left.current.ms : SHOW_FOR_MS);
    return () => {
      clearTimeout(timer);
      if (left.current.id === toast.id) left.current = { id: toast.id, ms: Math.max(1000, left.current.ms - (Date.now() - started)) };
    };
  }, [toast, paused]);

  const value = useMemo(() => notify, [notify]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className={s.toastRegion} role="status" aria-live="polite" aria-atomic="true">
        {toast ? (
          <div
            key={toast.id}
            className={s.toast}
            onPointerEnter={() => setPaused(true)}
            onPointerLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setPaused(false);
            }}
          >
            <span className={s.toastIcon} aria-hidden><CheckCircle2 /></span>
            <span className={s.toastText}>{toast.message}</span>
            <IconButton label="Close message" size="small" tooltip="top" onClick={close}>
              <X aria-hidden />
            </IconButton>
          </div>
        ) : null}
      </div>
    </ToastContext.Provider>
  );
}

/** Shows a confirmation, for example `notify("Asha Rao added.")`. Without a provider it does nothing. */
export function useNotify(): (message: string) => void {
  return useContext(ToastContext) ?? noop;
}

function noop() {}
